import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import React, { FC, useCallback, useState } from 'react'
import Slider from 'react-slick'
import styled from 'styled-components'

import { VideoSlide } from '../../features/car-details-card/components/VideoSlide'
import { VideoProps } from '../../features/car/types'
import { createMediaStyles } from '../../lib/styled-helpers'
import { useLoopCallback } from '../../lib/use-loop-callback'
import { Icon } from '../icon'
import { Image, Props as ImageProps } from '../image'
import { ImageWithZoom } from '../preview-slider/ImageWithZoom'
import { PreviewSlide } from '../preview-slider/PreviewSlide'
import { Slide } from './Slide'

type SliderSlickProps = {
  images?: ImageProps[]
  videos?: VideoProps[]
  activeSlide?: number
  isInPopup: boolean
  sliderRef?: any
  onSlideChange?: (index: number) => void
  settingsSlider?: {
    autoPlay?: boolean
    autoPlaySpeed?: number
    circularScroll?: boolean
    controls?: 'dots' | 'preview' | null
  }
}

export const SliderSlick: FC<SliderSlickProps> = ({
  images,
  videos,
  activeSlide = 0,
  isInPopup,
  sliderRef,
  onSlideChange,
  settingsSlider
}) => {
  const setActiveImageCallback = useLoopCallback((index) => () => onSlideChange && onSlideChange(index), [])
  const exitPreview = useCallback(() => onSlideChange && onSlideChange(0), [onSlideChange])
  const [isSwipingDisabled, setIsSwipingDisabled] = useState(false)

  const settingsArrow = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    height: '32px',
    width: '32px',
    borderRadius: '50%',
    background: 'rgb(77, 77, 77)',
    top: 'calc(50% - 16px)',
    cursor: 'pointer',
    zIndex: '10'
  }
  function NextArrow(props: any) {
    const { style, onClick } = props
    return (
      <NextArrowInner
        style={{
          ...style,
          ...settingsArrow
        }}
        onClick={onClick}
      >
        <Icon name="arrow-right" size={0.8} color={'white'} />
      </NextArrowInner>
    )
  }
  function PrevArrow(props: any) {
    const { style, onClick } = props
    return (
      <PrevArrowInner
        style={{
          ...style,
          ...settingsArrow
        }}
        onClick={onClick}
      >
        <Icon name="arrow-left" size={0.8} color={'white'} />
      </PrevArrowInner>
    )
  }

  return (
    <SliderWrapper isInPopup={isInPopup} className={'slider-wrapper'}>
      <Slider
        arrows={true}
        infinite={settingsSlider && settingsSlider.circularScroll}
        nextArrow={<NextArrow />}
        prevArrow={<PrevArrow />}
        ref={sliderRef}
        initialSlide={activeSlide}
        afterChange={(index) => onSlideChange && isInPopup && onSlideChange(index)}
        autoplay={settingsSlider && settingsSlider.autoPlay}
        autoplaySpeed={settingsSlider && settingsSlider.autoPlaySpeed && settingsSlider.autoPlaySpeed * 1000}
        dots={settingsSlider?.controls === 'dots'}
      >
        {images &&
          !isInPopup &&
          images.map((image, index) => {
            return (
              <SliderInner key={index}>
                <Slide onClick={isInPopup ? () => {} : setActiveImageCallback(index)}>
                  <Image sources={image.sources} src={image.src} alt={image.alt} fit={image.fit} ratio={image.ratio} />
                </Slide>
              </SliderInner>
            )
          })}

        {images &&
          isInPopup &&
          images.map(({ sources, src, alt, fit, ratio }, index) => {
            return (
              <ImageWithZoom
                isVisible={activeSlide === index}
                onTouchClick={exitPreview}
                onIsZoomedChange={(value) => {
                  setIsSwipingDisabled(value)
                }}
                src={src}
                alt={alt}
                fit={fit}
                key={index}
                ratio={ratio}
                onClickOutside={exitPreview}
              />
            )
          })}

        {videos &&
          !images &&
          videos.map(({ type, link, view }, index) => {
            return (
              <PreviewSlide onClickOutside={() => {}} key={index}>
                <VideoSlide type={type} link={link} view={view} isActive={index === activeSlide} />
              </PreviewSlide>
            )
          })}
      </Slider>
    </SliderWrapper>
  )
}

const SliderInner = styled.div`
  position: relative;
`

const SliderWrapper = styled.div<{ isInPopup: boolean }>`
  position: relative;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  z-index: 0;
  .slick-slider {
    display: flex;
    justify-content: center;
    width: ${({ isInPopup, theme }) => (isInPopup ? '100%' : '100%')};
  }
  .slick-list {
    width: 100%;
  }
`

const NextArrowInner = styled.div`
  ${() =>
    createMediaStyles(() => ({
      phone: `right: 0`,
      tablet: `right: 2.4rem`
    }))}
  &:hover {
    opacity: 0.8;
  }
`

const PrevArrowInner = styled.div`
  ${() =>
    createMediaStyles(() => ({
      phone: `left: 0`,
      tablet: `left: 2.4rem`
    }))}
  &:hover {
    opacity: 0.8;
  }
`
